import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import styled, { keyframes } from "styled-components";
import { IsFeatureReady } from "src/constants/features";
import {
    SectionLabel,
    SourceChipRow,
    SourceChip,
    DetailContent,
    MediaPlaceholder,
    GoalProgressWrap,
    GoalProgressHeader,
    GoalProgressTrack,
    GoalProgressFill,
    GoalProgressMeta,
    ConfettiLayer,
    ConfettiPiece,
    StackList,
    StackListItem,
    StackListNumber,
    StackListText,
    DetailRows,
    ModalCard,
    ModalHeaderBlock,
    ModalScroll,
    ModalFooter,
    ModalHeaderTop,
    ModalTitleBlock,
    ModalTitle,
    ModalHeroPills,
    ModalHeroPill,
    ModalSection,
    ModalSectionHeader,
    ModalSectionTitle,
    ModalSectionNote,
    ModalPrimaryButton,
    ModalCancelButton,
    TodayButton,
} from "..";
import {
    MeetingParticipantCount,
    MeetingParticipantPopover,
    MeetingParticipantPopoverContent,
    PlayIcon,
} from "src/components";
import {
    AUDIENCE_COLORS,
    getCalendarItemSourceLabel,
    getCompanyUserName,
    TASK_REPEAT_LABEL,
    TASK_REPEAT_OPTIONS,
} from "../../calendarData";
import { getItemTypeLabel } from "../../itemTypes";
import type {
    CalendarAttachment,
    CalendarItem,
    CalendarUser,
    ChecklistItemEntry,
    CompanyUser,
    ManagedItemType,
    ThemeMode,
} from "../../types";
import {
    getDisplayAudienceLabel,
    getVisibilitySubsetAssignments,
    hasScheduledCalendarFields,
    hasActionCalendarFields,
    getAutomationFeature,
    getItemPermissions,
    getUserPermissions,
    getReminderLabel,
    formatInTimezone,
    formatScheduledItemTime,
    formatTaskDueTime,
    statusLabel,
    priorityLabelText,
    checklistPriorityTone,
    getGoalProgress,
    normaliseAttachments,
    getCalendarItemDisplayColor
} from "../../utils/calendarHelpers";
import { uppercaseWords } from "utils/helpers";
import * as PERMISSIONS from "src/constants/permissions";
import { getMoodDetails } from "constants/moods";
import { SurveyType } from "src/constants/survey";
import IconRating from "src/img/survey/star.svg";
import IconSatisfaction from "src/img/survey/smile.svg";
import IconYesNo from "src/img/survey/icons/yes-no.webp";
import IconFeedBack from "src/img/survey/feedback.svg";
import AttachmentPlaceholder from "src/img/new/attach-image.svg";
import DownloadIcon from "src/img/download-white2.png";

const NoticeMedia = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: rgba(15, 23, 42, 0.06);
`;

const DetailFieldGrid = styled.div`
    display: grid;
    gap: 0.8rem;
`;

const DetailField = styled.div`
    display: grid;
    gap: 0.25rem;
`;

const DetailFieldLabel = styled.div`
    color: var(--calendar-text-soft);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

const DetailFieldValue = styled.div`
    color: var(--calendar-text);
    font-size: 14px;
    line-height: 1.45;
`;

const SurveyTypeCard = styled.div`
    display: flex;
    align-items: center;
    gap: 0.85rem;
    border: 1px solid var(--calendar-border);
    border-radius: var(--calendar-radius-card);
    background: var(--calendar-surface-secondary);
    padding: 0.9rem;
`;

const SurveyTypeIconWrap = styled.div`
    width: 46px;
    height: 46px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    border-radius: var(--calendar-radius-control);
    background: var(--calendar-surface);
    border: 1px solid var(--calendar-border);
`;

const SurveyTypeIcon = styled.img`
    width: 34px;
    height: 34px;
    object-fit: contain;
`;

const ChecklistPriorityGroup = styled.div`
    display: grid;
    gap: 0.55rem;
`;

const ChecklistPriorityBadge = styled.span<{ background: string; color: string }>`
    justify-self: start;
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 0 0.65rem;
    border-radius: var(--calendar-radius-badge);
    background: ${p => p.background};
    color: ${p => p.color};
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
`;

const AutomationContentGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 294px;
    gap: 1rem;
    align-items: start;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const EmailPreviewCard = styled.div`
    border: 1px solid var(--calendar-border);
    border-radius: var(--calendar-radius-card);
    background: #fff;
    aspect-ratio: 9 / 16;
    width: 100%;
    box-sizing: border-box;
    padding: 12px;
    overflow: hidden;
    box-shadow: 0 14px 30px rgba(15, 23, 42, 0.12);
`;

const EmailPreviewFrame = styled.div`
    width: 360px;
    min-height: 640px;
    transform: scale(0.75);
    transform-origin: top left;
    color: #111827;
    background: #fff;
`;

const EmailPreviewBanner = styled.img`
    display: block;
    width: 100%;
    height: auto;
`;

const EmailPreviewFallback = styled.div`
    height: 100%;
    display: grid;
    place-items: center;
    padding: 1rem;
    text-align: center;
    color: #64748b;
    font-size: 12px;
    line-height: 1.4;
`;

const moodGradientShift = keyframes`
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
`;

const MoodCard = styled.div<{ gradient: string }>`
    display: grid;
    justify-items: center;
    gap: 0.45rem;
    border-radius: var(--calendar-radius-card);
    padding: 1.35rem;
    background: ${p => p.gradient};
    background-size: 280% 280%;
    animation: ${moodGradientShift} 8s ease infinite;
    color: #fff;
    box-shadow: 0 18px 36px rgba(15, 23, 42, 0.18);
`;

const MoodEmoji = styled.div`
    font-size: 4.2rem;
    line-height: 1;
`;

const MoodLabel = styled.div`
    font-size: 2rem;
    font-weight: 800;
`;

const CenteredDetailText = styled.div`
    text-align: center;
    width: 100%;
`;

const TaskCompleteState = styled.div`
    padding: 0.85rem;
    border-radius: var(--calendar-radius-card);
    background: rgba(34, 197, 94, 0.13);
    color: #15803d;
    font-weight: 800;
    text-align: center;
`;

const NoticeVideo = styled.video`
    width: 100%;
    height: 100%;
    background: #000;
`;

const NoticeCarouselGrid = styled.div`
    width: calc(100% - 24px);
    height: calc(100% - 24px);
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
`;

const NoticeCarouselImage = styled.img`
    width: 100%;
    height: 100%;
    min-height: 0;
    object-fit: cover;
    border-radius: var(--calendar-radius-card);
`;

const NoticeCarouselVideo = styled.video`
    width: 100%;
    height: 100%;
    min-height: 0;
    background: #000;
    border-radius: var(--calendar-radius-card);
`;

const AttachmentPreviewList = styled.div`
    display: flex;
    gap: 0.75rem;
    overflow-x: auto;
    margin-top: 0.85rem;
    padding-bottom: 0.35rem;
    width: 100%;
`;

const AttachmentPreviewCard = styled.div`
    display: grid;
    grid-template-rows: 4rem auto;
    gap: 0.45rem;
    flex: 0 0 7.25rem;
    min-width: 0;
`;

const AttachmentMediaBox = styled.div`
    position: relative;
    height: 4rem;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    border: 1px solid var(--calendar-border);
    border-radius: var(--calendar-radius-control);
    background: var(--calendar-surface-secondary);
`;

const AttachmentVideoBadge = styled.div`
    position: absolute;
    right: 0.25rem;
    bottom: 0.25rem;
    display: inline-flex;
    pointer-events: none;

    svg {
        width: 1.35rem;
        height: 1.35rem;
    }
`;

const AttachmentImagePreview = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const AttachmentVideoPreview = styled.video`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: #000;
`;

const AttachmentFileDetails = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 1.65rem;
    align-items: center;
    gap: 0.35rem;
`;

const AttachmentFileName = styled.div`
    overflow: hidden;
    color: var(--calendar-text);
    font-size: 11px;
    font-weight: 700;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const AttachmentDownloadButton = styled.a`
    display: inline-flex;
    width: 1.65rem;
    height: 1.65rem;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: var(--calendar-accent);
    cursor: pointer;

    &:hover {
        filter: brightness(0.94);
    }

    &:focus-visible {
        outline: 2px solid var(--calendar-text);
        outline-offset: 2px;
    }
`;

const AttachmentDownloadIcon = styled.img`
    width: 0.85rem;
    height: 0.85rem;
    object-fit: contain;
`;

const getAttachmentExtension = (attachment: CalendarAttachment): string => {
    const extension = new URL(attachment.s3Url).pathname.split(".").pop();
    return extension ? extension.toLowerCase() : "";
};

const IMAGE_ATTACHMENT_EXTENSIONS = new Set(["avif", "gif", "jpeg", "jpg", "png", "svg", "webp"]);
const VIDEO_ATTACHMENT_EXTENSIONS = new Set(["mov", "mp4", "m4v", "ogv", "webm"]);

type AttachmentVideoThumbnailProps = {
    attachment: CalendarAttachment;
};

const AttachmentVideoThumbnail = ({ attachment }: AttachmentVideoThumbnailProps) => {
    const [thumbnail, setThumbnail] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        const video = document.createElement("video");
        video.crossOrigin = "anonymous";
        video.muted = true;
        video.preload = "auto";

        const captureThumbnail = () => {
            if (cancelled || !video.videoWidth || !video.videoHeight) {
                return;
            }

            try {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                const context = canvas.getContext("2d");
                if (!context) {
                    return;
                }
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                setThumbnail(canvas.toDataURL("image/jpeg", 0.82));
            } catch (error) {
                console.error(error);
            }
        };

        video.addEventListener("loadeddata", captureThumbnail, { once: true });
        video.src = attachment.s3Url;
        video.load();

        return () => {
            cancelled = true;
            video.pause();
            video.removeAttribute("src");
            video.load();
        };
    }, [attachment.s3Url]);

    if (thumbnail) {
        return <AttachmentImagePreview src={thumbnail} alt={attachment.filename} />;
    }

    return (
        <AttachmentVideoPreview muted preload="metadata" poster={AttachmentPlaceholder}>
            <source src={attachment.s3Url} />
        </AttachmentVideoPreview>
    );
};

const VIEWABLE_AUTOMATION_FEATURE_PERMISSIONS: Record<string, number> = {
    noticeboard: PERMISSIONS.NOTICEBOARD,
    emailCampaign: PERMISSIONS.EMAIL_CAMPAIGNS,
    survey: PERMISSIONS.SURVEYS,
    poll: PERMISSIONS.POLLS,
};

type DetailModalProps = {
    themeMode: ThemeMode;
    item: CalendarItem;
    timezone: string;
    itemTypes: ManagedItemType[];
    user: CalendarUser | null | undefined;
    companyUsers: CompanyUser[];
    onClose: () => void;
    onEdit: (item: CalendarItem) => void;
    onViewFeature: (item: CalendarItem) => void;
};

const getOrdinalSuffix = (day: number): string => {
    if (day >= 11 && day <= 13) return "th";
    if (day % 10 === 1) return "st";
    if (day % 10 === 2) return "nd";
    if (day % 10 === 3) return "rd";
    return "th";
};

const formatAutomationDateTime = (value?: string | null): string => {
    if (!value) return "Not set";

    const dateTimeMatch = value.match(/^(\d{4}-\d{2}-\d{2})(?:T(\d{2}:\d{2}))?/);
    const dateTime = dateTimeMatch
        ? dayjs(`${dateTimeMatch[1]}T${dateTimeMatch[2] || "00:00"}`)
        : dayjs(value);

    if (!dateTime.isValid()) return value;

    const dayOfMonth = dateTime.date();
    const timeFormat = dateTime.minute() === 0 ? "h A" : "h:mm A";
    return `${dateTime.format(`${timeFormat} MMMM D`)}${getOrdinalSuffix(dayOfMonth)}, ${dateTime.format("YYYY")}`;
};

const formatAutomationDate = (value?: string | null): string => {
    if (!value) return "Not set";
    const date = dayjs(value);
    return date.isValid() ? date.format("D MMM YYYY") : value;
};

const getChecklistPriorityRank = (priority?: string): number => {
    const normalisedPriority = (priority || "").toLowerCase();
    if (normalisedPriority === "high") return 0;
    if (normalisedPriority === "medium") return 1;
    if (normalisedPriority === "low") return 2;
    return 3;
};

const CHECKLIST_PRIORITY_GROUPS: Array<{ key: ChecklistItemEntry["priority"]; label: string }> = [
    { key: "high", label: "High" },
    { key: "medium", label: "Medium" },
    { key: "low", label: "Low" },
    { key: "none", label: "No Priority" },
];

const getChecklistPriorityGroupKey = (priority: ChecklistItemEntry["priority"]): ChecklistItemEntry["priority"] => {
    if (priority === "high" || priority === "medium" || priority === "low") return priority;
    return "none";
};

const getSurveyTypeIcon = (surveyType?: string): string | null => {
    if (surveyType === SurveyType.RATING) return IconRating;
    if (surveyType === SurveyType.SATISFACTION) return IconSatisfaction;
    if (surveyType === SurveyType.YESNO) return IconYesNo;
    if (surveyType === SurveyType.FEEDBACK) return IconFeedBack;
    return null;
};

const getMoodGradient = (moodType?: string): string => {
    const normalisedMood = (moodType || "").toLowerCase();
    if (normalisedMood === "excellent") return "linear-gradient(135deg, #14b8a6, #22c55e, #facc15, #38bdf8)";
    if (normalisedMood === "happy" || normalisedMood === "great") return "linear-gradient(135deg, #f59e0b, #f97316, #ec4899, #8b5cf6)";
    if (normalisedMood === "ok" || normalisedMood === "okay") return "linear-gradient(135deg, #38bdf8, #6366f1, #a78bfa, #22d3ee)";
    if (normalisedMood === "poor") return "linear-gradient(135deg, #64748b, #3b82f6, #818cf8, #c084fc)";
    if (normalisedMood === "bad") return "linear-gradient(135deg, #475569, #7c3aed, #db2777, #f43f5e)";
    return "linear-gradient(135deg, #5b6ef5, #7c3aed, #42e7b6, #f59e0b)";
};

const canViewAutomationFeature = (automationFeature: string, user: CalendarUser | null | undefined): boolean => {
    const requiredPermission = VIEWABLE_AUTOMATION_FEATURE_PERMISSIONS[automationFeature];
    if (!requiredPermission) return false;

    const userPermissions = getUserPermissions(user);
    return userPermissions.includes(PERMISSIONS.ACCOUNT_OWNER_PERMISSION) || userPermissions.includes(requiredPermission);
};

export function DetailModal({ themeMode, item, timezone, itemTypes, user, companyUsers, onClose, onEdit, onViewFeature }: DetailModalProps) {
    const attachments = normaliseAttachments(item.attachments);
    const [downloadingAttachments, setDownloadingAttachments] = useState(false);
    const isScheduledItem = hasScheduledCalendarFields(item);
    const isActionItem = hasActionCalendarFields(item);
    const automationFeature = getAutomationFeature(item);
    const itemTypeValue = item.itemTypeUuid || item.taskType;
    const heroTypeLabel = itemTypeValue
        ? getItemTypeLabel(itemTypeValue, itemTypes)
        : getCalendarItemSourceLabel(item.source, "Calendar Item");
    const meetingSummary = IsFeatureReady.publicMeetings() && item.meeting && "meetingUuid" in item.meeting
        ? item.meeting
        : null;
    const baseMeetingUrl = meetingSummary?.meetingUrl || item.meetingUrl;
    const meetingUrl = baseMeetingUrl ? (() => {
        const url = new URL(baseMeetingUrl, window.location.origin);
        if (meetingSummary?.occurrence?.occurrenceUuid) {
            url.searchParams.set("occurrence", meetingSummary.occurrence.occurrenceUuid);
        }
        return url.toString();
    })() : "";
    const meetingStatus = meetingSummary?.occurrence?.status || meetingSummary?.status;
    const meetingInternalParticipants = meetingSummary?.internalParticipantUuids.map(participantUuid => ({
        participantUuid,
        companyUser: companyUsers.find(companyUser => companyUser.uuid === participantUuid),
    })) || [];
    const displayTypeLabel = automationFeature
        ? getCalendarItemSourceLabel(item.source, heroTypeLabel)
        : heroTypeLabel;

    const downloadAttachment = async (attachment: CalendarAttachment) => {
        try {
            const response = await fetch(attachment.s3Url);
            if (!response.ok) {
                throw new Error("Attachment download failed.");
            }
            const objectUrl = URL.createObjectURL(await response.blob());
            const downloadLink = document.createElement("a");
            downloadLink.href = objectUrl;
            downloadLink.download = attachment.filename;
            downloadLink.click();
            URL.revokeObjectURL(objectUrl);
        } catch (error) {
            console.error(error);
        }
    };

    const handleAttachmentDownload = (event: React.MouseEvent<HTMLAnchorElement>, attachment: CalendarAttachment) => {
        event.preventDefault();
        downloadAttachment(attachment);
    };

    const downloadAllAttachments = async (attachmentValues: CalendarAttachment[]) => {
        setDownloadingAttachments(true);
        try {
            await Promise.all(attachmentValues.map(downloadAttachment));
        } finally {
            setDownloadingAttachments(false);
        }
    };

    const renderAttachmentsHeader = (attachmentValues: CalendarAttachment[]): React.ReactNode => (
        <ModalSectionHeader>
            <ModalSectionTitle>Attachments</ModalSectionTitle>
            <TodayButton
                type="button"
                disabled={downloadingAttachments}
                style={{ minWidth: 92 }}
                onClick={() => downloadAllAttachments(attachmentValues)}
            >
                {downloadingAttachments ? "Downloading..." : "Download All"}
            </TodayButton>
        </ModalSectionHeader>
    );

    const renderAttachments = (attachmentValues: CalendarAttachment[]): React.ReactNode => (
        <AttachmentPreviewList>
            {attachmentValues.map((attachment) => {
                const extension = getAttachmentExtension(attachment);
                const isImage = IMAGE_ATTACHMENT_EXTENSIONS.has(extension);
                const isVideo = VIDEO_ATTACHMENT_EXTENSIONS.has(extension);
                return (
                    <AttachmentPreviewCard key={attachment.uuid}>
                        <AttachmentMediaBox>
                            {isImage ? (
                                <AttachmentImagePreview
                                    src={attachment.s3Url}
                                    alt={attachment.filename}
                                    onError={({ currentTarget }) => { currentTarget.src = AttachmentPlaceholder; }}
                                />
                            ) : isVideo ? (
                                <>
                                    <AttachmentVideoThumbnail attachment={attachment} />
                                    <AttachmentVideoBadge aria-hidden="true"><PlayIcon /></AttachmentVideoBadge>
                                </>
                            ) : (
                                <AttachmentImagePreview src={AttachmentPlaceholder} alt="File attachment" />
                            )}
                        </AttachmentMediaBox>
                        <AttachmentFileDetails>
                            <AttachmentFileName title={attachment.filename}>{attachment.filename}</AttachmentFileName>
                            <AttachmentDownloadButton
                                href={attachment.s3Url}
                                download={attachment.filename}
                                title={`Download ${attachment.filename}`}
                                aria-label={`Download ${attachment.filename}`}
                                onClick={(event) => handleAttachmentDownload(event, attachment)}
                            >
                                <AttachmentDownloadIcon src={DownloadIcon} alt="" aria-hidden="true" />
                            </AttachmentDownloadButton>
                        </AttachmentFileDetails>
                    </AttachmentPreviewCard>
                );
            })}
        </AttachmentPreviewList>
    );
    const displayTypeColor = getCalendarItemDisplayColor(item);
    const visibilityLabel = getDisplayAudienceLabel(item.audience);
    const companyUserByUuid = useMemo(() => new Map(companyUsers.map(companyUser => [companyUser.uuid, companyUser])), [companyUsers]);
    const visibilitySubsetAssignments = useMemo(() => (
        getVisibilitySubsetAssignments(item).map(assignment => ({
            ...assignment,
            selectedUsers: assignment.selectedEmployeeUuids.map(uuid => ({
                uuid,
                companyUser: companyUserByUuid.get(uuid),
            })),
        }))
    ), [companyUserByUuid, item]);
    const permissions = getItemPermissions(item, user);
    const automationDetail = item.automationDetail || {};
    const showEditButton = !automationFeature && permissions.canEdit;
    const showViewButton = canViewAutomationFeature(automationFeature, user);
    const goalProgress = getGoalProgress(item);
    const itemStartDate = isScheduledItem ? item.startAt : item.startDate || item.dueDate;
    const itemEndDate = isScheduledItem ? item.endAt : item.endDate || item.startDate || item.dueDate;
    const renderNoticeboardMedia = (): React.ReactNode => {
        const postType = (automationDetail.postType || "").toLowerCase();
        const carouselItems = [...(automationDetail.carousel || [])]
            .sort((left, right) => (left.carouselOrder ?? 0) - (right.carouselOrder ?? 0))
            .filter(mediaItem => mediaItem.imageUrl || mediaItem.videoUrl);
        const imageUrl = automationDetail.image?.imageUrl || automationDetail.media?.find(mediaItem => mediaItem.type === "image")?.url;
        const videoUrl = automationDetail.video?.videoUrl || automationDetail.media?.find(mediaItem => mediaItem.type === "video")?.url;
        const videoPoster = automationDetail.video?.previewImageUrl || undefined;

        return (
            <MediaPlaceholder themeMode={themeMode} ratio="16 / 9">
                {postType === "carousel" && carouselItems.length > 0 ? (
                    <NoticeCarouselGrid>
                        {carouselItems.map((mediaItem) => (
                            mediaItem.videoUrl ? (
                                <NoticeCarouselVideo
                                    key={mediaItem.uuid || mediaItem.videoUrl}
                                    controls
                                    preload="metadata"
                                    poster={mediaItem.previewImageUrl || undefined}
                                >
                                    <source src={mediaItem.videoUrl} type="video/mp4" />
                                </NoticeCarouselVideo>
                            ) : (
                                <NoticeCarouselImage
                                    key={mediaItem.uuid || mediaItem.imageUrl}
                                    src={mediaItem.imageUrl}
                                    alt={mediaItem.mediaType || "Noticeboard carousel media"}
                                />
                            )
                        ))}
                    </NoticeCarouselGrid>
                ) : postType === "video" && videoUrl ? (
                    <NoticeVideo controls preload="metadata" poster={videoPoster || undefined}>
                        <source src={videoUrl} type="video/mp4" />
                    </NoticeVideo>
                ) : imageUrl ? (
                    <NoticeMedia src={imageUrl} alt={automationDetail.title || item.title} />
                ) : (
                    <></>
                )}
            </MediaPlaceholder>
        );
    };

    const renderAutomationContent = (): React.ReactNode => {
        if (!automationFeature) return null;

        if (automationFeature === "noticeboard") {
            const postType = (automationDetail.postType || "").toLowerCase();
            const hasMedia = postType === "image" || postType === "video" || postType === "carousel";
            const scheduledDate = formatAutomationDateTime(item.dueDate);
            const detailAttachments = normaliseAttachments(automationDetail.attachments || item.attachments);
            return (
                <>
                    <ModalSection themeMode={themeMode}>
                        <ModalSectionHeader>
                            <ModalSectionTitle>Noticeboard Post</ModalSectionTitle>
                            <ModalSectionNote>Read-only automation data from Noticeboard.</ModalSectionNote>
                        </ModalSectionHeader>
                        <DetailRows>
                            <div><strong>Post Title:</strong> {automationDetail.postTitle || item.title}</div>
                            <div><strong>Post Message:</strong> {automationDetail.postMessage || item.description}</div>
                            <div><strong>Scheduled For:</strong> {getDisplayAudienceLabel(item.audience)}</div>
                            <div><strong>Scheduled Date & Time:</strong> {scheduledDate}</div>
                            <div><strong>Comments:</strong> {automationDetail.commentsEnabled ? "On" : "Off"}</div>
                            <div><strong>Posted to Social Accounts:</strong> {(automationDetail.socialAccounts || []).join(", ") || "None"}</div>
                            <div><strong>Created By:</strong> {item.createdByName}</div>
                        </DetailRows>
                    </ModalSection>
                    { hasMedia && (
                        <ModalSection themeMode={themeMode}>
                            <ModalSectionHeader>
                                <ModalSectionTitle>Scheduled Media</ModalSectionTitle>
                            </ModalSectionHeader>
                            {renderNoticeboardMedia()}
                        </ModalSection>
                    )}
                    {detailAttachments.length > 0 && (
                        <ModalSection themeMode={themeMode}>
                            {renderAttachmentsHeader(detailAttachments)}
                            {renderAttachments(detailAttachments)}
                        </ModalSection>
                    )}
                </>
            );
        }

        if (automationFeature === "survey") {
            const surveyStartDate = formatAutomationDateTime(automationDetail.startDate);
            const surveyEndDate = formatAutomationDateTime(automationDetail.finishDate || automationDetail.endDate);
            const surveyType = automationDetail.surveyType || "Survey";
            const surveyTypeIcon = getSurveyTypeIcon(automationDetail.surveyType);
            return (
                <ModalSection themeMode={themeMode}>
                    <ModalSectionHeader>
                        <ModalSectionTitle>Survey</ModalSectionTitle>
                        <ModalSectionNote>Read-only automation data from Survey.</ModalSectionNote>
                    </ModalSectionHeader>
                    <SurveyTypeCard>
                        {surveyTypeIcon && (
                            <SurveyTypeIconWrap>
                                <SurveyTypeIcon src={surveyTypeIcon} alt={`${surveyType} survey`} />
                            </SurveyTypeIconWrap>
                        )}
                        <DetailField>
                            <DetailFieldLabel>Survey Type</DetailFieldLabel>
                            <DetailFieldValue>{surveyType}</DetailFieldValue>
                        </DetailField>
                    </SurveyTypeCard>
                    <DetailFieldGrid>
                        <DetailField>
                            <DetailFieldLabel>Start Date</DetailFieldLabel>
                            <DetailFieldValue>{surveyStartDate}</DetailFieldValue>
                        </DetailField>
                        <DetailField>
                            <DetailFieldLabel>End Date</DetailFieldLabel>
                            <DetailFieldValue>{surveyEndDate}</DetailFieldValue>
                        </DetailField>
                        <DetailField>
                            <DetailFieldLabel>Anonymous</DetailFieldLabel>
                            <DetailFieldValue>{automationDetail.isAnonymous ?? automationDetail.anonymous ? "Yes" : "No"}</DetailFieldValue>
                        </DetailField>
                    </DetailFieldGrid>
                    <div>
                        <strong>Survey Questions</strong>
                        <StackList style={{ marginTop: 10 }}>
                            {(automationDetail.questions || []).map((question, index) => (
                                <StackListItem key={`${index + 1}-${question}`}>
                                    <StackListNumber>{index + 1}</StackListNumber>
                                    <StackListText>{question}</StackListText>
                                </StackListItem>
                            ))}
                        </StackList>
                    </div>
                </ModalSection>
            );
        }

        if (automationFeature === "poll") {
            const pollImageUrl = automationDetail.image?.imageUrl || automationDetail.imageUrl || automationDetail.pollImage;
            const pollStart = formatAutomationDateTime(automationDetail.startsAt);
            const pollEnd = formatAutomationDateTime(automationDetail.endsAt);
            return (
                <>
                    <ModalSection themeMode={themeMode}>
                        <ModalSectionHeader>
                            <ModalSectionTitle>Poll</ModalSectionTitle>
                            <ModalSectionNote>Read-only automation data from Poll.</ModalSectionNote>
                        </ModalSectionHeader>
                        {pollImageUrl && (
                            <MediaPlaceholder themeMode={themeMode} ratio="16 / 9" style={{ marginBottom: 14 }}>
                                <NoticeMedia src={pollImageUrl} alt={automationDetail.pollName || item.title} />
                            </MediaPlaceholder>
                        )}
                        <DetailFieldGrid>
                            <DetailField>
                                <DetailFieldLabel>Start Date</DetailFieldLabel>
                                <DetailFieldValue>{pollStart}</DetailFieldValue>
                            </DetailField>
                            <DetailField>
                                <DetailFieldLabel>End Date</DetailFieldLabel>
                                <DetailFieldValue>{pollEnd}</DetailFieldValue>
                            </DetailField>
                        </DetailFieldGrid>
                        <div>
                            <strong>Answer Options</strong>
                            <StackList style={{ marginTop: 10 }}>
                                {(automationDetail.options || []).map((option, index) => (
                                    <StackListItem key={option}>
                                        <StackListNumber>{index + 1}</StackListNumber>
                                        <StackListText>{option}</StackListText>
                                    </StackListItem>
                                ))}
                            </StackList>
                        </div>
                    </ModalSection>
                </>
            );
        }

        if (automationFeature === "emailCampaign") {
            const emailContent = automationDetail.emailContent;
            const emailHeaderImageUrl = automationDetail.headerImageUrl;
            return (
                <ModalSection themeMode={themeMode}>
                    <ModalSectionHeader>
                        <ModalSectionTitle>Email Campaign</ModalSectionTitle>
                        <ModalSectionNote>Read-only automation data from Email Campaign.</ModalSectionNote>
                    </ModalSectionHeader>
                    <AutomationContentGrid>
                        <DetailFieldGrid>
                            <DetailField>
                                <DetailFieldLabel>Subject</DetailFieldLabel>
                                <DetailFieldValue>{automationDetail.subject || item.description || "Not set"}</DetailFieldValue>
                            </DetailField>
                            <DetailField>
                                <DetailFieldLabel>Status</DetailFieldLabel>
                                <DetailFieldValue>{uppercaseWords(automationDetail.emailStatus || item.status)}</DetailFieldValue>
                            </DetailField>
                            <DetailField>
                                <DetailFieldLabel>Number of Recipients</DetailFieldLabel>
                                <DetailFieldValue>{String(automationDetail.sentToCount ?? automationDetail.recipientCount ?? "Not set")}</DetailFieldValue>
                            </DetailField>
                            <DetailField>
                                <DetailFieldLabel>Scheduled Date & Time</DetailFieldLabel>
                                <DetailFieldValue>{formatAutomationDateTime(automationDetail.scheduleAt || itemStartDate)}</DetailFieldValue>
                            </DetailField>
                            {automationDetail.sentAt && (
                                <DetailField>
                                    <DetailFieldLabel>Sent At</DetailFieldLabel>
                                    <DetailFieldValue>{automationDetail.sentAt}</DetailFieldValue>
                                </DetailField>
                            )}
                        </DetailFieldGrid>
                        <EmailPreviewCard>
                            {emailContent ? (
                                <EmailPreviewFrame>
                                    {emailHeaderImageUrl && <EmailPreviewBanner src={emailHeaderImageUrl} alt="" />}
                                    <div dangerouslySetInnerHTML={{ __html: emailContent }} />
                                </EmailPreviewFrame>
                            ) : (
                                <EmailPreviewFallback>Email template preview unavailable.</EmailPreviewFallback>
                            )}
                        </EmailPreviewCard>
                    </AutomationContentGrid>
                </ModalSection>
            );
        }

        if (automationFeature === "goal" || automationFeature === "teamGoal") {
            const teamGoalEmployees = automationDetail.teamGoalEmployees || [];
            const goalStartDate = dayjs(automationDetail.startDate || itemStartDate);
            const goalEndDate = dayjs(automationDetail.finishDate || itemEndDate);
            return (
                <ModalSection themeMode={themeMode}>
                    <ModalSectionHeader>
                        <ModalSectionTitle>{automationFeature === "teamGoal" ? "Team Goal" : "Goal"}</ModalSectionTitle>
                        <ModalSectionNote>Read-only automation data from {automationFeature === "teamGoal" ? "Team Goal" : "Goal"}.</ModalSectionNote>
                    </ModalSectionHeader>
                    <DetailFieldGrid>
                        <DetailField>
                            <DetailFieldLabel>Goal Description</DetailFieldLabel>
                            <DetailFieldValue>{automationDetail.message || automationDetail.goalDescription || item.description || "Not set"}</DetailFieldValue>
                        </DetailField>
                        <DetailField>
                            <DetailFieldLabel>Goal Reward</DetailFieldLabel>
                            <DetailFieldValue>{automationDetail.reward || "Not set"}</DetailFieldValue>
                        </DetailField>
                        <DetailField>
                            <DetailFieldLabel>Goal Dates</DetailFieldLabel>
                            <DetailFieldValue>{goalStartDate.format("D MMM YYYY")} to {goalEndDate.format("D MMM YYYY")}</DetailFieldValue>
                        </DetailField>
                        {automationFeature === "teamGoal" && (
                            <DetailField>
                                <DetailFieldLabel>Team Goal Complete</DetailFieldLabel>
                                <DetailFieldValue>{automationDetail.isTeamGoalCompleted ? "Yes" : "No"}</DetailFieldValue>
                            </DetailField>
                        )}
                    </DetailFieldGrid>
                    {teamGoalEmployees.length > 0 && (
                        <div style={{ marginTop: 12 }}>
                            <strong>Team Members</strong>
                            <StackList style={{ marginTop: 10 }}>
                                {teamGoalEmployees.map((employee, index) => (
                                    <StackListItem key={employee.employeeUuid || `${employee.employeeName}-${index}`}>
                                        <StackListNumber>{index + 1}</StackListNumber>
                                        <StackListText>
                                            {employee.employeeName || [employee.firstName, employee.lastName].filter(Boolean).join(" ")}
                                            {employee.completedAt ? ` · completed ${dayjs(employee.completedAt).format("D MMM YYYY")}` : ""}
                                        </StackListText>
                                    </StackListItem>
                                ))}
                            </StackList>
                        </div>
                    )}
                    <GoalProgressWrap>
                        {goalProgress >= 100 && (
                            <ConfettiLayer>
                                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map((index) => (
                                    <ConfettiPiece
                                        key={`confetti-${index}`}
                                        left={8 + (index * 9)}
                                        delay={index * 0.09}
                                        duration={1.8 + ((index % 3) * 0.22)}
                                        color={["#5b6ef5", "#7c3aed", "#42e7b6", "#f59e0b"][index % 4]}
                                    />
                                ))}
                            </ConfettiLayer>
                        )}
                        <GoalProgressHeader>
                            <SectionLabel>Goal Progress</SectionLabel>
                            <div style={{ fontWeight: 800, color: goalProgress >= 100 ? "#22c55e" : "var(--calendar-text)" }}>
                                {goalProgress >= 100 ? "Complete · 100%" : `${goalProgress}%`}
                            </div>
                        </GoalProgressHeader>
                        <GoalProgressTrack themeMode={themeMode}>
                            <GoalProgressFill progress={goalProgress} />
                        </GoalProgressTrack>
                        <GoalProgressMeta>
                            <span>{goalStartDate.format("D MMM")}</span>
                            <span>{goalProgress >= 100 ? "Goal completed" : "Progress based on goal date range"}</span>
                            <span>{goalEndDate.format("D MMM")}</span>
                        </GoalProgressMeta>
                    </GoalProgressWrap>
                </ModalSection>
            );
        }

        if (automationFeature === "habit") {
            const habitImageUrl = automationDetail.image?.imageUrl || automationDetail.imageUrl;
            return (
                <ModalSection themeMode={themeMode}>
                    <ModalSectionHeader>
                        <ModalSectionTitle>Habit</ModalSectionTitle>
                        <ModalSectionNote>Read-only automation data from Habits.</ModalSectionNote>
                    </ModalSectionHeader>
                    {habitImageUrl && (
                        <MediaPlaceholder themeMode={themeMode} ratio="1 / 1" style={{ margin: "0 auto 14px", width: "50%" }}>
                            <NoticeMedia src={habitImageUrl} alt={automationDetail.title || item.title} />
                        </MediaPlaceholder>
                    )}
                    <DetailFieldGrid>
                        <DetailField>
                            <DetailFieldLabel>Message</DetailFieldLabel>
                            <DetailFieldValue>{automationDetail.message || item.description || "Not set"}</DetailFieldValue>
                        </DetailField>
                        <DetailField>
                            <DetailFieldLabel>Habit Type</DetailFieldLabel>
                            <DetailFieldValue>{uppercaseWords(automationDetail.habitType || "Habit")}</DetailFieldValue>
                        </DetailField>
                        <DetailField>
                            <DetailFieldLabel>Status</DetailFieldLabel>
                            <DetailFieldValue>{automationDetail.status || item.status}</DetailFieldValue>
                        </DetailField>
                        <DetailField>
                            <DetailFieldLabel>Date</DetailFieldLabel>
                            <DetailFieldValue>{formatAutomationDate(automationDetail.date || itemStartDate)}</DetailFieldValue>
                        </DetailField>
                    </DetailFieldGrid>
                </ModalSection>
            );
        }

        if (automationFeature === "mood") {
            const moodPresentation = getMoodDetails(automationDetail.moodType || null);
            const loggedAt = formatAutomationDateTime(automationDetail.createdAt || itemStartDate);
            return (
                <ModalSection themeMode={themeMode}>
                    <ModalSectionHeader>
                        <ModalSectionTitle>Mood Tracker</ModalSectionTitle>
                        <ModalSectionNote>Read-only automation data from Mood Tracker.</ModalSectionNote>
                    </ModalSectionHeader>
                    <MoodCard gradient={getMoodGradient(automationDetail.moodType)}>
                        <CenteredDetailText>Your selected mood for today:</CenteredDetailText>
                        <MoodEmoji>{moodPresentation?.emoji || ""}</MoodEmoji>
                        <MoodLabel>{moodPresentation?.label || ""}</MoodLabel>
                    </MoodCard>
                    <CenteredDetailText>{moodPresentation?.message || ""}</CenteredDetailText>
                    <CenteredDetailText>
                        <DetailFieldLabel>Logged At</DetailFieldLabel>
                        <DetailFieldValue>{loggedAt}</DetailFieldValue>
                    </CenteredDetailText>
                </ModalSection>
            );
        }

        if (automationFeature === "checklist") {
            const detailAttachments = normaliseAttachments(automationDetail.attachments || item.attachments);
            const checklistDueDate = formatAutomationDate(automationDetail.dueDate);
            const checklistImageUrl = automationDetail.imageUrl;
            const checklistItems = [...(automationDetail.items || [])].sort((left, right) => (
                getChecklistPriorityRank(left.priority) - getChecklistPriorityRank(right.priority)
                || left.order - right.order
            ));
            const checklistItemsByPriority = CHECKLIST_PRIORITY_GROUPS.map(priorityGroup => ({
                ...priorityGroup,
                items: checklistItems.filter(checklistItem => (
                    getChecklistPriorityGroupKey(checklistItem.priority) === priorityGroup.key
                )),
            })).filter(priorityGroup => priorityGroup.items.length > 0);
            const selectedTeamMemberCount = visibilitySubsetAssignments.reduce((total, assignment) => (
                total + assignment.selectedUsers.length
            ), 0);
            const teamMemberCount = automationDetail.teamMemberCount ?? selectedTeamMemberCount;
            const completedTaskCount = automationDetail.itemsDone ?? checklistItems.filter(checklistItem => checklistItem.isDone).length;
            const totalTaskCount = automationDetail.itemsTotal ?? checklistItems.length;
            const checklistComplete = totalTaskCount > 0 && completedTaskCount >= totalTaskCount;
            return (
                <>
                    <ModalSection themeMode={themeMode}>
                        <ModalSectionHeader>
                            <ModalSectionTitle>{automationDetail.checklistTitle || item.title}</ModalSectionTitle>
                            <ModalSectionNote>Read-only automation data from Checklist.</ModalSectionNote>
                        </ModalSectionHeader>
                        {checklistImageUrl && (
                            <MediaPlaceholder themeMode={themeMode} ratio="16 / 9">
                                <NoticeMedia src={checklistImageUrl} alt={automationDetail.checklistTitle || item.title} />
                            </MediaPlaceholder>
                        )}
                        <DetailFieldGrid>
                            <DetailField>
                                <DetailFieldLabel>Due Date</DetailFieldLabel>
                                <DetailFieldValue>{checklistDueDate}</DetailFieldValue>
                            </DetailField>
                            {teamMemberCount > 0 && (
                                <DetailField>
                                    <DetailFieldLabel>Team Checklist</DetailFieldLabel>
                                    <DetailFieldValue>{teamMemberCount} {teamMemberCount === 1 ? "user" : "users"}</DetailFieldValue>
                                </DetailField>
                            )}
                            <DetailField>
                                <DetailFieldLabel>Tasks Completed</DetailFieldLabel>
                                <DetailFieldValue>{completedTaskCount}/{totalTaskCount} Done</DetailFieldValue>
                            </DetailField>
                        </DetailFieldGrid>
                        {checklistComplete && <TaskCompleteState>All tasks completed</TaskCompleteState>}
                        {checklistItems.length > 0 && (
                            <div>
                                <strong>Tasks</strong>
                                <StackList style={{ marginTop: 10 }}>
                                    {checklistItemsByPriority.map(priorityGroup => {
                                        const priorityTone = checklistPriorityTone[priorityGroup.label];
                                        return (
                                            <ChecklistPriorityGroup key={priorityGroup.key}>
                                                <ChecklistPriorityBadge
                                                    background={priorityTone.background}
                                                    color={priorityTone.color}
                                                >
                                                    {priorityGroup.label}
                                                </ChecklistPriorityBadge>
                                                {priorityGroup.items.map(checklistItem => (
                                                    <StackListItem key={`${checklistItem.order}-${checklistItem.details}`}>
                                                        <StackListNumber>{checklistItem.order}</StackListNumber>
                                                        <StackListText>
                                                            {checklistItem.details}
                                                            {checklistItem.isDone ? " · Done" : ""}
                                                        </StackListText>
                                                    </StackListItem>
                                                ))}
                                            </ChecklistPriorityGroup>
                                        );
                                    })}
                                </StackList>
                            </div>
                        )}
                    </ModalSection>
                    {detailAttachments.length > 0 && (
                        <ModalSection themeMode={themeMode}>
                            {renderAttachmentsHeader(detailAttachments)}
                            {renderAttachments(detailAttachments)}
                        </ModalSection>
                    )}
                </>
            );
        }

        return null;
    };

    return (
        <ModalCard themeMode={themeMode}>
            <ModalHeaderBlock themeMode={themeMode}>
                <ModalHeaderTop>
                    <ModalTitleBlock>
                        <ModalTitle>{item.title}</ModalTitle>
                    </ModalTitleBlock>
                    <ModalHeroPills>
                        <ModalHeroPill background={`${AUDIENCE_COLORS[item.audience] || "var(--calendar-accent)"}22`} color={AUDIENCE_COLORS[item.audience] || "var(--calendar-accent)"}>
                            {visibilityLabel}
                        </ModalHeroPill>
                        <ModalHeroPill background={`${displayTypeColor}22`} color={displayTypeColor}>
                            {displayTypeLabel}
                        </ModalHeroPill>
                    </ModalHeroPills>
                </ModalHeaderTop>
            </ModalHeaderBlock>
            <ModalScroll>
                {automationFeature ? renderAutomationContent() : (
                    <>
                        <ModalSection themeMode={themeMode}>
                            <ModalSectionHeader>
                                <ModalSectionTitle>Item Details</ModalSectionTitle>
                            </ModalSectionHeader>
                            <DetailContent>
                                <div><strong>Type:</strong> {heroTypeLabel}</div>
                                {isScheduledItem && <div><strong>Time:</strong> {formatScheduledItemTime(item, timezone)}</div>}
                                {isScheduledItem && <div><strong>Date:</strong> {formatInTimezone(item.startAt, timezone, { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</div>}
                                {isActionItem && <div><strong>Starts:</strong> {dayjs(item.startDate || item.dueDate).format("ddd D MMM YYYY")} · {formatTaskDueTime({ dueDate: item.startDate || item.dueDate, dueTime: item.startTime || item.dueTime, allDay: item.allDay }, timezone)}</div>}
                                {isActionItem && <div><strong>Ends:</strong> {dayjs(item.endDate || item.startDate || item.dueDate).format("ddd D MMM YYYY")} · {formatTaskDueTime({ dueDate: item.endDate || item.startDate || item.dueDate, dueTime: item.endTime || item.startTime || item.dueTime, allDay: item.allDay }, timezone)}</div>}
                                <div><strong>Status:</strong> {statusLabel(item.status)}</div>
                                <div><strong>Visibility:</strong> {getDisplayAudienceLabel(item.audience)}</div>
                                {visibilitySubsetAssignments.map(({ groupKey, group, selectedUsers }) => (
                                    <div key={groupKey}>
                                        <strong>Assigned {group.label}:</strong>
                                        <SourceChipRow style={{ marginTop: 6 }}>
                                            {selectedUsers.map(({ uuid, companyUser }) => (
                                                <SourceChip
                                                    key={uuid}
                                                    background={`${group.color}22`}
                                                    color={group.color}
                                                    title={companyUser?.email || uuid}
                                                >
                                                    {companyUser ? getCompanyUserName(companyUser) : uuid}
                                                </SourceChip>
                                            ))}
                                        </SourceChipRow>
                                    </div>
                                ))}
                                {isActionItem && <div><strong>Priority:</strong> {priorityLabelText(item.priority)}</div>}
                                {isActionItem && <div><strong>Repeat:</strong> {TASK_REPEAT_LABEL[item.repeat || (item.recurring ? TASK_REPEAT_OPTIONS.EVERY_WEEK : TASK_REPEAT_OPTIONS.NONE)]}</div>}
                                {item.reminderOffset && item.reminderOffset !== "none" && <div><strong>Reminder:</strong> {getReminderLabel(item.reminderOffset)}</div>}
                                <div><strong>Created by:</strong> {item.createdByName}</div>
                            </DetailContent>
                        </ModalSection>
                        {IsFeatureReady.publicMeetings() && item.meeting && (
                            <ModalSection themeMode={themeMode}>
                                <ModalSectionHeader>
                                    <ModalSectionTitle>Me Meets</ModalSectionTitle>
                                    <ModalSectionNote>Meeting access and participant details.</ModalSectionNote>
                                </ModalSectionHeader>
                                <DetailContent>
                                    {meetingUrl && (
                                        <div>
                                            <strong>Meeting link:</strong>{" "}
                                            <a
                                                href={meetingUrl}
                                                onClick={event => {
                                                    event.preventDefault();
                                                    const portalMeetingUrl = new URL(meetingUrl, window.location.origin);
                                                    portalMeetingUrl.searchParams.set("source", "portal");
                                                    window.open(
                                                        portalMeetingUrl.toString(),
                                                        `meeting-${meetingSummary?.meetingUuid || "window"}`,
                                                        "popup,width=1280,height=800,resizable=yes"
                                                    );
                                                }}
                                            >
                                                {meetingUrl}
                                            </a>
                                        </div>
                                    )}
                                    {meetingStatus && <div><strong>Status:</strong> {uppercaseWords(meetingStatus)}</div>}
                                    <div><strong>Timezone:</strong> {item.meeting.timezone}</div>
                                    <div>
                                        <strong>Password protection:</strong>{" "}
                                        {item.meeting.passwordEnabled ? "Enabled" : "Disabled"}
                                    </div>
                                    {meetingSummary && <div><strong>Guest approval:</strong> Required</div>}
                                    <div>
                                        <strong>Internal participants:</strong>{" "}
                                        {meetingInternalParticipants.length > 0 ? (
                                            <MeetingParticipantPopover>
                                                <MeetingParticipantCount
                                                    type="button"
                                                    aria-label={`Show ${meetingInternalParticipants.length} internal participants`}
                                                >
                                                    {meetingInternalParticipants.length}
                                                </MeetingParticipantCount>
                                                <MeetingParticipantPopoverContent role="tooltip">
                                                    <SourceChipRow>
                                                        {meetingInternalParticipants.map(({ participantUuid, companyUser }) => (
                                                            <SourceChip
                                                                key={participantUuid}
                                                                background="rgba(91, 110, 245, .14)"
                                                                color="#5b6ef5"
                                                                title={companyUser?.email || participantUuid}
                                                            >
                                                                {companyUser ? getCompanyUserName(companyUser) : participantUuid}
                                                            </SourceChip>
                                                        ))}
                                                    </SourceChipRow>
                                                </MeetingParticipantPopoverContent>
                                            </MeetingParticipantPopover>
                                        ) : 0}
                                    </div>
                                    <div>
                                        <strong>External guests:</strong>{" "}
                                        {item.meeting.guestEmails.length > 0 ? (
                                            <MeetingParticipantPopover>
                                                <MeetingParticipantCount
                                                    type="button"
                                                    aria-label={`Show ${item.meeting.guestEmails.length} external guests`}
                                                >
                                                    {item.meeting.guestEmails.length}
                                                </MeetingParticipantCount>
                                                <MeetingParticipantPopoverContent role="tooltip">
                                                    <SourceChipRow>
                                                        {item.meeting.guestEmails.map(guestEmail => (
                                                            <SourceChip
                                                                key={guestEmail}
                                                                background="rgba(34, 197, 94, .14)"
                                                                color="#16a34a"
                                                                title={guestEmail}
                                                            >
                                                                {guestEmail}
                                                            </SourceChip>
                                                        ))}
                                                    </SourceChipRow>
                                                </MeetingParticipantPopoverContent>
                                            </MeetingParticipantPopover>
                                        ) : 0}
                                    </div>
                                </DetailContent>
                            </ModalSection>
                        )}
                        {(item.description || (!item.meeting && item.meetingUrl) || item.location || item.invites || item.url) && (
                            <ModalSection themeMode={themeMode}>
                                <ModalSectionHeader>
                                    <ModalSectionTitle>People & Links</ModalSectionTitle>
                                </ModalSectionHeader>
                                <DetailContent>
                                    {item.description && <div><strong>Notes:</strong> {item.description}</div>}
                                    {item.location && <div><strong>Location:</strong> {item.location}</div>}
                                    {item.invites && <div><strong>Invites:</strong> {item.invites}</div>}
                                    {item.url && <div><strong>URL:</strong> <a href={item.url} target="_blank" rel="noopener noreferrer">{item.url}</a></div>}
                                    {!item.meeting && item.meetingUrl && <div><strong>Meeting:</strong> <a href={item.meetingUrl} target="_blank" rel="noopener noreferrer">{item.meetingUrl}</a></div>}
                                </DetailContent>
                            </ModalSection>
                        )}
                        {attachments.length > 0 && (
                            <ModalSection themeMode={themeMode}>
                                {renderAttachmentsHeader(attachments)}
                                {renderAttachments(attachments)}
                            </ModalSection>
                        )}
                    </>
                )}
            </ModalScroll>
            <ModalFooter themeMode={themeMode} style={{ justifyContent: "flex-end" }}>
                <div style={{ display: "flex", gap: 8 }}>
                    <ModalCancelButton themeMode={themeMode} type="button" onClick={onClose}>Close</ModalCancelButton>
                    {showViewButton ? (
                        <ModalPrimaryButton themeMode={themeMode} type="button" onClick={() => onViewFeature(item)}>
                            {`View ${displayTypeLabel}`}
                        </ModalPrimaryButton>
                    ) : showEditButton && (
                        <ModalPrimaryButton themeMode={themeMode} type="button" onClick={() => onEdit(item)}>Edit</ModalPrimaryButton>
                    )}
                </div>
            </ModalFooter>
        </ModalCard>
    );
}
